import { useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Package, Pencil, Trash2, UploadCloud } from 'lucide-react';
import { RELEASE_CHANNELS, compareVersions, type OwnerReleaseDto, type OwnerReleaseOverviewDto, type ReleaseChannel } from '@his/shared';
import { del, get, uploadWithProgress } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { formatBytes, shortSha } from '@/lib/format-bytes';
import { dateOnly, dateTime } from '@/lib/utils';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { DataTable, Dialog, PageHeader } from '@/components/ui/layout';
import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Checkbox, Field, Input, Textarea } from '@/components/ui/primitives';
import { ReleaseNotes } from '@/components/ui/release-notes';
import { useToast } from '@/components/ui/toast';
import { TotpField, isValidTotp } from './OwnerConfirm';

export const OWNER_RELEASES_KEY = ['owner', 'releases'] as const;

export const VERSION_PATTERN = /^\d+\.\d+\.\d+(-[0-9A-Za-z.]+)?$/;

const RELEASE_CHANNEL_LABEL: Record<ReleaseChannel, string> = { STABLE: 'Stable', BETA: 'Beta' };

function ChannelBadge({ channel }: { channel: ReleaseChannel }) {
  return <Badge variant={channel === 'BETA' ? 'warning' : 'default'}>{RELEASE_CHANNEL_LABEL[channel]}</Badge>;
}

function ProgressBar({ value, label }: { value: number; label: string }) {
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-muted" role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(value)}>
      <div className="h-full rounded-full bg-primary transition-[width]" style={{ width: `${Math.min(100, Math.max(0, value))}%` }} />
    </div>
  );
}

/**
 * Upload (plan §3.2): the `.lpk` from `npm run release:offline`, streamed
 * with progress; the server re-verifies the signature and refuses a
 * duplicate or a version below the channel's current latest. The
 * authenticator code travels with the form — a wrong code costs a
 * re-upload, which is the price of every owner mutation being step-up 2FA.
 */
function UploadCard({ onUploaded }: { onUploaded: () => void }) {
  const toast = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [channel, setChannel] = useState<ReleaseChannel>('STABLE');
  const [notes, setNotes] = useState('');
  const [isRequired, setIsRequired] = useState(false);
  const [minimumAllowedVersion, setMinimumAllowedVersion] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [progress, setProgress] = useState<number | null>(null);
  const [inputKey, setInputKey] = useState(0);

  const upload = useMutation({
    mutationFn: (formData: FormData) => uploadWithProgress<OwnerReleaseDto>('/owner/releases', formData, (fraction) => setProgress(fraction)),
  });

  const minimumInvalid = minimumAllowedVersion !== '' && !VERSION_PATTERN.test(minimumAllowedVersion);
  const canSubmit = file !== null && !upload.isPending && !minimumInvalid && (!isRequired || minimumAllowedVersion !== '') && isValidTotp(totpCode);

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (!file || !canSubmit) return;
    const formData = new FormData();
    formData.append('channel', channel);
    if (notes.trim()) formData.append('notes', notes);
    formData.append('isRequired', isRequired ? 'true' : 'false');
    if (minimumAllowedVersion) formData.append('minimumAllowedVersion', minimumAllowedVersion);
    formData.append('totpCode', totpCode);
    formData.append('file', file);
    setProgress(0);
    upload.mutate(formData, {
      onSuccess: (release) => {
        toast.success(`Version ${release.version} published`, `${RELEASE_CHANNEL_LABEL[release.channel]} channel — offline facilities are alerted on their next check-in`);
        setFile(null);
        setNotes('');
        setIsRequired(false);
        setMinimumAllowedVersion('');
        setTotpCode('');
        setProgress(null);
        setInputKey((k) => k + 1);
        onUploaded();
      },
      onError: (err) => {
        setProgress(null);
        setTotpCode('');
        toast.error('Upload refused', describeError(err));
      },
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm">
          <UploadCloud className="h-4 w-4 text-primary" aria-hidden /> Upload a release
        </CardTitle>
        <CardDescription>
          Pick the .lpk produced by <span className="font-mono">npm run release:offline</span>. The server re-verifies the signature and refuses duplicates or a
          version below the channel&apos;s current latest. Offline facilities are alerted as soon as it is published.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-4" noValidate>
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Package (.lpk)" htmlFor="release-file" hint={file ? `${file.name} · ${formatBytes(file.size)}` : undefined}>
              <Input key={inputKey} id="release-file" type="file" accept=".lpk" disabled={upload.isPending} onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
            </Field>
            <Field label="Channel" htmlFor="release-channel">
              <ChoiceButtons
                id="release-channel"
                value={channel}
                disabled={upload.isPending}
                onChange={setChannel}
                options={RELEASE_CHANNELS.map((value) => ({ value, label: RELEASE_CHANNEL_LABEL[value] }))}
              />
            </Field>
          </div>

          <Field label="Release notes (markdown)" htmlFor="release-notes">
            <Textarea
              id="release-notes"
              rows={6}
              className="font-mono text-xs"
              placeholder={'## What changed\n- New discharge summary layout\n- Fixes for the radiology worklist'}
              value={notes}
              disabled={upload.isPending}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Field>

          <div className="grid gap-4 md:grid-cols-2">
            <Checkbox
              className="pt-6"
              checked={isRequired}
              disabled={upload.isPending}
              onCheckedChange={setIsRequired}
              label="Mark as required"
              description="Installs below the minimum allowed version go read-only until they update."
            />
            <Field
              label="Minimum allowed version"
              htmlFor="release-minimum"
              hint={minimumInvalid ? 'Use the form 1.4.0 or 1.4.0-beta.1' : isRequired && minimumAllowedVersion === '' ? 'A required release needs a minimum allowed version.' : undefined}
            >
              <Input
                id="release-minimum"
                className="font-mono"
                placeholder="1.4.0"
                value={minimumAllowedVersion}
                disabled={upload.isPending}
                onChange={(e) => setMinimumAllowedVersion(e.target.value.trim())}
              />
            </Field>
          </div>

          <TotpField value={totpCode} onChange={setTotpCode} />

          {progress !== null && (
            <div className="space-y-1">
              <ProgressBar value={progress * 100} label="Upload progress" />
              <p className="text-xs text-muted-foreground">{progress >= 1 ? 'Verifying signature and storing the package…' : `Uploading ${Math.round(progress * 100)}%`}</p>
            </div>
          )}

          <div className="flex justify-end">
            <Button type="submit" loading={upload.isPending} disabled={!canSubmit}>
              <UploadCloud className="h-4 w-4" /> Publish release
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function DeleteReleaseDialog({ release, onClose }: { release: OwnerReleaseDto | null; onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [totpCode, setTotpCode] = useState('');
  const remove = useMutation({
    mutationFn: () => del<void>(`/owner/releases/${encodeURIComponent(release?.id ?? '')}`, { body: { totpCode } }),
    onSuccess: () => {
      toast.success(`Release ${release?.version} deleted`);
      setTotpCode('');
      void queryClient.invalidateQueries({ queryKey: OWNER_RELEASES_KEY });
      onClose();
    },
    onError: (err) => toast.error('Could not delete the release', describeError(err)),
  });

  const onVersion = release?.tenantsOnVersion.length ?? 0;

  return (
    <Dialog
      open={release !== null}
      onClose={onClose}
      title={`Delete release ${release?.version ?? ''}?`}
      description={`The package file is removed from the server and the landing page stops offering it. Facilities already on this version are unaffected.${onVersion > 0 ? ` ${onVersion} facility(ies) currently report this version.` : ''}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="destructive" loading={remove.isPending} disabled={!isValidTotp(totpCode)} onClick={() => remove.mutate()}>
            <Trash2 className="h-4 w-4" /> Delete
          </Button>
        </>
      }
    >
      <TotpField value={totpCode} onChange={setTotpCode} />
    </Dialog>
  );
}

function TenantsByVersionPanel({ overview }: { overview: OwnerReleaseOverviewDto }) {
  const groups = useMemo(() => {
    const byVersion = new Map<string, OwnerReleaseOverviewDto['tenantVersions']>();
    for (const tenant of overview.tenantVersions) {
      const key = tenant.runningVersion ?? 'unknown';
      byVersion.set(key, [...(byVersion.get(key) ?? []), tenant]);
    }
    return [...byVersion.entries()].sort(([a], [b]) => (a === 'unknown' ? 1 : b === 'unknown' ? -1 : compareVersions(b, a)));
  }, [overview.tenantVersions]);
  const latestStable = overview.releases.find((r) => r.channel === 'STABLE')?.version ?? null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Facilities by version</CardTitle>
        <CardDescription>Every offline facility and the version it reported at its last check-in.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {groups.length === 0 && <p className="text-sm text-muted-foreground">No offline facilities yet.</p>}
        {groups.map(([version, tenants]) => {
          const behind = latestStable !== null && version !== 'unknown' && compareVersions(version, latestStable) < 0;
          return (
            <div key={version} className="rounded-md border bg-muted/30 p-3">
              <div className="mb-2 flex items-center gap-2">
                <span className="font-mono text-sm font-semibold">{version === 'unknown' ? 'Never reported' : `v${version}`}</span>
                {version === latestStable ? <Badge variant="success">Latest stable</Badge> : behind ? <Badge variant="warning">Behind</Badge> : null}
                <span className="text-xs text-muted-foreground">
                  {tenants.length} {tenants.length === 1 ? 'facility' : 'facilities'}
                </span>
              </div>
              <ul className="space-y-1">
                {tenants.map((tenant) => (
                  <li key={tenant.id} className="flex items-center justify-between gap-2 text-xs">
                    <span className="truncate">
                      <span className="font-mono">{tenant.slug}</span> <span className="text-muted-foreground">· {tenant.name}</span>
                    </span>
                    <span className="flex-none text-muted-foreground">{tenant.lastSeenAt ? dateTime(tenant.lastSeenAt) : 'Never seen'}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

/** Owner console → Releases (plan §3.2, §3.6). */
export function OwnerReleasesPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [deleting, setDeleting] = useState<OwnerReleaseDto | null>(null);
  const [viewingNotes, setViewingNotes] = useState<OwnerReleaseDto | null>(null);

  const overview = useQuery({
    queryKey: OWNER_RELEASES_KEY,
    queryFn: () => get<OwnerReleaseOverviewDto>('/owner/releases'),
    staleTime: 10_000,
    retry: false,
  });

  return (
    <>
      <PageHeader
        title="Releases"
        description="Signed offline packages for facilities on their own server. Upload a new version, mark it required, and see which one runs what."
        actions={
          <Button variant="outline" onClick={() => navigate('/owner/releases/announce')}>
            Announce a version
          </Button>
        }
      />

      <div className="grid gap-4 xl:grid-cols-[1.4fr_1fr]">
        <UploadCard onUploaded={() => void queryClient.invalidateQueries({ queryKey: OWNER_RELEASES_KEY })} />
        {overview.data ? <TenantsByVersionPanel overview={overview.data} /> : <Card />}
      </div>

      <Card className="mt-4">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm">
            <Package className="h-4 w-4 text-primary" aria-hidden /> Published releases
          </CardTitle>
          <CardDescription>Newest first. Click a version to read its notes.</CardDescription>
        </CardHeader>
        <CardContent>
          {overview.isError ? (
            <p className="text-sm text-destructive">{describeError(overview.error)}</p>
          ) : (
            <DataTable
              rows={overview.data?.releases}
              loading={overview.isLoading} refreshing={overview.isFetching}
              error={overview.error}
              rowKey={(row) => row.id}
              empty="No release has been published yet."
              columns={[
                {
                  header: 'Version',
                  sort: (row) => row.version,
                  cell: (row) => (
                    <button type="button" className="font-mono font-semibold underline-offset-2 hover:underline" onClick={() => setViewingNotes(row)}>
                      {row.version}
                    </button>
                  ),
                },
                { header: 'Channel', sort: (row) => row.channel, cell: (row) => <ChannelBadge channel={row.channel} /> },
                {
                  header: 'Size',
                  sort: (row) => (row.hasFile === false ? null : row.size),
                  cell: (row) => (row.hasFile === false ? <Badge variant="muted">Announced, no file yet</Badge> : <span className="font-mono">{formatBytes(row.size)}</span>),
                  align: 'right',
                },
                {
                  header: 'SHA-256',
                  cell: (row) => (
                    <span className="font-mono text-xs text-muted-foreground" title={row.sha256}>
                      {shortSha(row.sha256)}
                    </span>
                  ),
                },
                { header: 'Downloads', sort: (row) => row.downloadCount, cell: (row) => <span className="font-mono">{row.downloadCount}</span>, align: 'right' },
                {
                  header: 'Required',
                  sort: (row) => row.isRequired,
                  cell: (row) =>
                    row.isRequired ? (
                      <div className="space-y-0.5">
                        <Badge variant="destructive">{row.minimumAllowedVersion ? `Min ${row.minimumAllowedVersion}` : 'Required'}</Badge>
                        {row.requiredBy && <p className="text-xs text-muted-foreground">by {dateOnly(row.requiredBy)}</p>}
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    ),
                },
                { header: 'Published', sort: (row) => row.publishedAt, cell: (row) => <span className="whitespace-nowrap text-muted-foreground">{dateTime(row.publishedAt)}</span> },
                {
                  header: 'Facilities on it',
                  sort: (row) => row.tenantsOnVersion.length,
                  cell: (row) => <span title={row.tenantsOnVersion.map((t) => t.slug).join(', ')}>{row.tenantsOnVersion.length}</span>,
                  align: 'right',
                },
                {
                  header: '',
                  cell: (row) => (
                    <span className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Edit release" onClick={() => navigate(`/owner/releases/${encodeURIComponent(row.id)}/edit`)}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" aria-label="Delete release" onClick={() => setDeleting(row)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </span>
                  ),
                  align: 'right',
                },
              ]}
            />
          )}
        </CardContent>
      </Card>

      {deleting !== null && (
        <DeleteReleaseDialog release={deleting} onClose={() => setDeleting(null)} />
      )}
      <Dialog
        open={viewingNotes !== null}
        onClose={() => setViewingNotes(null)}
        title={`Release ${viewingNotes?.version ?? ''}`}
        description={viewingNotes ? `${RELEASE_CHANNEL_LABEL[viewingNotes.channel]} · ${viewingNotes.fileName} · ${formatBytes(viewingNotes.size)} · git ${viewingNotes.gitSha?.slice(0, 7) ?? '—'}` : undefined}
      >
        <ReleaseNotes notes={viewingNotes?.notes} />
        {viewingNotes && <p className="mt-4 break-all font-mono text-[11px] text-muted-foreground">sha256 {viewingNotes.sha256}</p>}
      </Dialog>
    </>
  );
}
